import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  login,
  logout,
  fetchMe,
  fetchInquiries,
  updateInquiryStatus,
  formatApiErrorDetail,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUSES = ["new", "contacted", "quoted", "closed"];

const statusStyle = {
  new: "bg-[var(--mx-copper)]/10 text-[var(--mx-copper)]",
  contacted: "bg-[var(--mx-teal)]/10 text-[var(--mx-teal)]",
  quoted: "bg-[var(--mx-ink)]/10 text-[var(--mx-ink)]",
  closed: "bg-[var(--mx-ink-3)]/15 text-[var(--mx-ink-2)]",
};

const fieldClass =
  "mt-2 h-12 rounded-lg border-[var(--mx-line-strong)] bg-[var(--mx-surface)] text-[var(--mx-ink)] placeholder:text-[var(--mx-ink-3)] focus-visible:border-[var(--mx-copper)] focus-visible:ring-2 focus-visible:ring-[var(--mx-copper)]/20";

const labelClass =
  "font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-2)]";

const LoginForm = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      onSuccess(user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--mx-surface-2)] px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-2xl border border-[var(--mx-line)] bg-[var(--mx-surface)] p-8 shadow-[0_24px_70px_-50px_rgba(27,23,20,0.5)]"
      >
        <div className="font-display text-2xl font-extrabold tracking-tight text-[var(--mx-ink)]">
          Metal<span className="text-[var(--mx-copper)]">ix</span> XT
        </div>
        <p className="mt-1 font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-2)]">
          Admin sign in
        </p>
        <div className="mt-8">
          <Label className={labelClass}>Email</Label>
          <Input
            data-testid="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@metalix-xt.com"
            className={fieldClass}
          />
        </div>
        <div className="mt-5">
          <Label className={labelClass}>Password</Label>
          <Input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={fieldClass}
          />
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 text-sm text-[var(--mx-copper)]">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-8 w-full rounded-full bg-[var(--mx-copper)] px-8 py-4 font-mono-tech text-xs uppercase tracking-widest text-white transition-opacity disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in →"}
        </button>
        <Link
          to="/"
          className="mt-6 block text-center font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-3)] transition-colors hover:text-[var(--mx-copper)]"
        >
          ← Back to site
        </Link>
      </form>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [items, setItems] = useState(null);

  const load = () =>
    fetchInquiries()
      .then(setItems)
      .catch(() => toast.error("Could not load enquiries."));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      const updated = await updateInquiryStatus(id, status);
      setItems((list) => list.map((it) => (it.id === id ? updated : it)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--mx-surface-2)] px-6 py-10 md:px-10">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-black uppercase tracking-tight text-[var(--mx-ink)]">
              Enquiries
            </h1>
            <p className="mt-1 font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-2)]">
              Signed in as {user.email}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="rounded-full border border-[var(--mx-line-strong)] px-5 py-2.5 font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink)] transition-colors hover:border-[var(--mx-copper)] hover:text-[var(--mx-copper)]"
            >
              Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="rounded-full bg-[var(--mx-charcoal)] px-5 py-2.5 font-mono-tech text-xs uppercase tracking-widest text-white transition-colors hover:bg-[var(--mx-copper)]"
            >
              Log out
            </button>
          </div>
        </div>

        <div data-testid="admin-inquiries" className="mt-10 space-y-4">
          {items === null && (
            <p className="font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-2)]">
              Loading…
            </p>
          )}
          {items !== null && items.length === 0 && (
            <p
              data-testid="admin-empty"
              className="rounded-2xl border border-[var(--mx-line)] bg-[var(--mx-surface)] p-10 text-center font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-2)]"
            >
              No enquiries yet
            </p>
          )}
          {items?.map((it) => (
            <div
              key={it.id}
              data-testid={`inquiry-row-${it.id}`}
              className="rounded-2xl border border-[var(--mx-line)] bg-[var(--mx-surface)] p-6"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-display text-xl font-bold text-[var(--mx-ink)]">
                    {it.name}
                    {it.company && (
                      <span className="ml-2 text-sm font-normal text-[var(--mx-ink-2)]">
                        · {it.company}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 font-mono-tech text-xs text-[var(--mx-ink-2)]">
                    {it.email} {it.phone && `· ${it.phone}`}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    data-testid={`inquiry-status-${it.id}`}
                    className={`rounded-full px-3 py-1 font-mono-tech text-[10px] uppercase tracking-widest ${statusStyle[it.status] || statusStyle.new}`}
                  >
                    {it.status}
                  </span>
                  <Select
                    value={it.status}
                    onValueChange={(v) => setStatus(it.id, v)}
                  >
                    <SelectTrigger
                      data-testid={`inquiry-status-select-${it.id}`}
                      className="h-9 w-[140px] rounded-lg border-[var(--mx-line-strong)] bg-[var(--mx-surface)] text-xs text-[var(--mx-ink)]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg border-[var(--mx-line-strong)] bg-[var(--mx-surface)] text-[var(--mx-ink)]">
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-2)]">
                <span>{it.commodity}</span>
                <span>{it.intent}</span>
                {it.volume && <span>{it.volume}</span>}
                <span className="text-[var(--mx-ink-3)]">
                  {new Date(it.created_at).toLocaleString()}
                </span>
              </div>
              {it.message && (
                <p className="mt-3 border-t border-[var(--mx-line)] pt-3 text-sm leading-relaxed text-[var(--mx-ink-2)]">
                  {it.message}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined); // undefined = checking

  useEffect(() => {
    fetchMe()
      .then(setUser)
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--mx-surface-2)] font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-2)]">
        Loading…
      </div>
    );
  }

  if (user === null) {
    return <LoginForm onSuccess={setUser} />;
  }

  return (
    <Dashboard
      user={user}
      onLogout={async () => {
        await logout().catch(() => {});
        setUser(null);
      }}
    />
  );
}
